import { lazy, Suspense } from 'react'

import { defineMessages } from 'react-intl'
import { useRouteMatch } from 'react-router'

import { Page } from '@webapp/core/components/Page'
import { useAppRoutes, useProcurifyIntl } from '@webapp/hooks'
import { expenseReportConfig } from '@webapp/modules/reports/config/reportConfigs'
import { useSecondaryCategoryDetailHeaderPresenter } from '@webapp/modules/reports/features/get-secondary-category-detail-header/presenters/UseSecondaryCategoryDetailHeaderPresenter'
import { type ReportDetailsPageParams } from '../types'

const ReportDetailViewChunk = lazy(() =>
  import('../../feature-sets/ReportDetailView').then(
    ({ ReportDetailView }) => ({
      default: ReportDetailView,
    })
  )
)

const scope = `procurify.modules.reports.pages.ExpensesBySecondaryCategoryReportDetailsPage`

const messages = defineMessages({
  pageTitle: {
    id: `${scope}.pageTitle`,
    defaultMessage: 'Expenses by department',
  },
})

export const ExpensesBySecondaryCategoryReportDetailsPage = () => {
  const intl = useProcurifyIntl()
  const { url } = useAppRoutes()
  const match = useRouteMatch<ReportDetailsPageParams>()

  const entityId = Number(match.params.id)

  // Breadcrumb entity name resolved independently of the line-item fetch
  // (BHV-022) — same query key as the Feature Set's header hook, so React
  // Query dedupes the fetch.
  const {
    data: { secondaryCategoryName },
  } = useSecondaryCategoryDetailHeaderPresenter({
    secondaryCategoryId: entityId,
  })

  return (
    <Page
      pageTitle={intl.formatMessage(messages.pageTitle)}
      breadcrumbs={[
        {
          link: url('ExpensesBySecondaryCategoryReportPage'),
          name: intl.formatMessage(messages.pageTitle),
        },
        { name: secondaryCategoryName ?? '', escapeCaseFormatting: true },
      ]}
    >
      <div data-testid='expenses-by-secondary-category-report-details-page'>
        <Suspense fallback={null}>
          <ReportDetailViewChunk
            reportConfig={expenseReportConfig}
            entityId={entityId}
          />
        </Suspense>
      </div>
    </Page>
  )
}
